import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Container } from '../layout/Container';
import { HeaderSection } from '../layout/CardsContainer';
import NavTab from '../components/NavTab';
import Button from '../components/Button';
import ENV_CONFIG from '../config/environment-unified';
import useTranslation from '../hooks/useTranslation';

const parseDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const TournamentEvents = () => {
  const history = useHistory();
  const isLoggedIn = useSelector(state => state.user.isLoggedIn);
  const user = useSelector(state => state.user.data);
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState(1); // NavTab is 1-based: 1=Discover, 2=My
  const tabs = [t('tournamentEvents.discover', 'Discover'), t('tournamentEvents.myEvents', 'My Tournament Events')];

  const [loading, setLoading] = useState(true);
  const [discoverEvents, setDiscoverEvents] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [livePovCountByEventId, setLivePovCountByEventId] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(new Set(['live', 'coming_soon', 'pending', 'finished']));

  // A lightweight clock to keep date-based statuses fresh while the page is open.
  const [clock, setClock] = useState(() => Date.now());

  useEffect(() => {
    let mounted = true;
    async function loadDiscover() {
      try {
        setLoading(true);
        // Use public events list endpoint (guest-friendly) and filter to tournament category.
        // Include past + completed so we can display Finished tournaments too (server default remains "recent only").
        const res = await axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/events?category=tournament&limit=50&includePast=true&includeCompleted=true`, {
          withCredentials: true
        });
        const raw = res.data?.events || [];
        if (!mounted) return;
        setDiscoverEvents(raw);

        // Map eventId -> number of live POVs (streams) linked to the event.
        // Used to distinguish Live vs Pending (tournament started but no POV yet).
        try {
          const streamsRes = await axios.get(ENV_CONFIG.STREAMS_ENDPOINT, { withCredentials: true });
          const streams = streamsRes.data || [];
          const map = {};
          for (const s of streams) {
            if (!s?.currentEventId) continue;
            const key = String(s.currentEventId);
            map[key] = (map[key] || 0) + 1;
          }
          if (mounted) setLivePovCountByEventId(map);
        } catch (e) {
          if (mounted) setLivePovCountByEventId({});
        }
      } catch (e) {
        console.error('❌ Failed to load tournament discover events:', e);
        toast.error(t('tournamentEvents.failedToLoad', 'Failed to load tournament events'));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadDiscover();
    return () => { mounted = false; };
  }, [t]);

  // Poll live POV counts so tournaments can move Pending -> Live without reload.
  useEffect(() => {
    let mounted = true;
    let timer = null;

    async function refreshLivePovCounts() {
      try {
        const streamsRes = await axios.get(ENV_CONFIG.STREAMS_ENDPOINT, { withCredentials: true });
        const streams = streamsRes.data || [];
        const map = {};
        for (const s of streams) {
          if (!s?.currentEventId) continue;
          const key = String(s.currentEventId);
          // Be defensive: if the API returns non-live streams, ignore them.
          if (s?.isLive === false) continue;
          map[key] = (map[key] || 0) + 1;
        }
        if (mounted) setLivePovCountByEventId(map);
      } catch (_) {
        // ignore polling failures
      }
    }

    refreshLivePovCounts();
    timer = setInterval(refreshLivePovCounts, 15000);
    return () => {
      mounted = false;
      if (timer) clearInterval(timer);
    };
  }, []);

  // Update clock periodically so "Finished" flips based on endDate.
  useEffect(() => {
    const timer = setInterval(() => setClock(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let mounted = true;
    async function loadMine() {
      if (!isLoggedIn) {
        setMyEvents([]);
        return;
      }

      try {
        // Merge "created" + "joined" (+ invitations can be added later)
        const [createdRes, joinedRes] = await Promise.all([
          axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/events/my-events`, { withCredentials: true }),
          axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/events/joined-events`, { withCredentials: true })
        ]);

        const created = createdRes.data?.events || [];
        const joined = joinedRes.data?.events || [];

        const combined = [...created, ...joined]
          .filter(ev => ev?.category?.slug === 'tournament')
          .reduce((acc, ev) => {
            if (!acc.some(x => String(x.id) === String(ev.id))) acc.push(ev);
            return acc;
          }, []);

        if (!mounted) return;
        setMyEvents(combined);
      } catch (e) {
        console.error('❌ Failed to load my tournament events:', e);
        toast.error(t('tournamentEvents.failedToLoadMine', 'Failed to load your tournament events'));
      }
    }

    loadMine();
    return () => { mounted = false; };
  }, [isLoggedIn, t]);

  const parsePrice = (ev) => {
    const n = typeof ev?.ticketPrice === 'string' ? parseFloat(ev.ticketPrice) : Number(ev?.ticketPrice);
    return Number.isFinite(n) ? n : 0;
  };

  const classify = useCallback((ev) => {
    const now = new Date(clock);
    const start = parseDate(ev?.startDate);
    const end = parseDate(ev?.endDate);
    const hasStart = !!start;
    const hasEnd = !!end;
    const hasStarted = hasStart && start.getTime() <= now.getTime();
    const hasEnded = hasEnd && end.getTime() <= now.getTime();

    // Finished only when explicitly completed OR endDate passed.
    // Do NOT mark finished just because tournamentActive=false (it's also false before start).
    if (ev?.status === 'completed' || ev?.status === 'cancelled') return 'finished';
    if (hasEnded) return 'finished';

    const livePov = livePovCountByEventId?.[String(ev.id)] || 0;

    // Based on schedule (start/end) and live POVs:
    // - Coming soon: startDate in the future
    // - Pending: within the event window but no POV live yet
    // - Live: at least one POV live
    if (livePov > 0 || ev?.status === 'active') return 'live';
    if (hasStart && !hasStarted) return 'coming_soon';
    if (hasStarted) return 'pending';

    // Fallback if dates are missing/invalid.
    return 'pending';
  }, [clock, livePovCountByEventId]);

  const matchesSearch = useCallback((ev) => {
    const q = String(searchQuery || '').trim().toLowerCase();
    if (!q) return true;
    const name = String(ev?.name || '').toLowerCase();
    const desc = String(ev?.description || '').toLowerCase();
    return name.includes(q) || desc.includes(q);
  }, [searchQuery]);

  const statusLabel = useCallback((k) => {
    switch (k) {
      case 'live': return t('tournamentEvents.status.live', 'Live');
      case 'coming_soon': return t('tournamentEvents.status.comingSoon', 'Coming soon');
      case 'pending': return t('tournamentEvents.status.pending', 'Pending');
      case 'finished': return t('tournamentEvents.status.finished', 'Finished');
      default: return String(k || '');
    }
  }, [t]);

  const isStatusEnabled = (status) => {
    try {
      return statusFilter.has(status);
    } catch {
      return true;
    }
  };

  const discoverGrouped = useMemo(() => {
    if (activeTab !== 1) return null;
    const list = (discoverEvents || [])
      .filter(ev => ev?.category?.slug === 'tournament')
      .filter(matchesSearch);
    const groups = { live: [], coming_soon: [], pending: [], finished: [] };
    for (const ev of list) {
      groups[classify(ev)].push(ev);
    }
    const sortByStart = (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
    groups.live.sort(sortByStart);
    groups.coming_soon.sort(sortByStart);
    groups.pending.sort(sortByStart);
    groups.finished.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
    return groups;
  }, [activeTab, classify, discoverEvents, matchesSearch]);

  const myFiltered = useMemo(() => {
    if (activeTab !== 2) return [];
    return (myEvents || [])
      .filter(ev => ev?.category?.slug === 'tournament')
      .filter(matchesSearch);
  }, [activeTab, matchesSearch, myEvents]);

  return (
    <Container style={{ paddingTop: '4rem', paddingBottom: '2rem' }}>
      <HeaderSection title={t('tournamentEvents.title', 'Tournament Events')}>
        <Link to="/events">
          <Button variant="secondary" size="small">{t('tournamentEvents.allEvents', 'All Events')}</Button>
        </Link>
      </HeaderSection>

      <NavTab list={tabs} index={activeTab} setIndex={(i) => {
        if (i === 2 && !isLoggedIn) {
          toast.info(t('tournamentEvents.pleaseLogin', 'Please login to view your tournament events'));
          history.push('/?noAuth');
          return;
        }
        setActiveTab(i);
      }} />

      <FilterBar>
        <SearchInput
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('tournamentEvents.searchPlaceholder', 'Search by name or description…')}
        />
        <Pills>
          {[
            { key: 'live', label: statusLabel('live') },
            { key: 'coming_soon', label: statusLabel('coming_soon') },
            { key: 'pending', label: statusLabel('pending') },
            { key: 'finished', label: statusLabel('finished') },
          ].map((s) => (
            <Pill
              key={s.key}
              active={statusFilter.has(s.key)}
              onClick={() => {
                setStatusFilter((prev) => {
                  const next = new Set(prev);
                  if (next.has(s.key)) next.delete(s.key);
                  else next.add(s.key);
                  return next.size ? next : prev; // keep at least one
                });
              }}
              type="button"
            >
              {s.label}
            </Pill>
          ))}
        </Pills>
      </FilterBar>

      {loading ? (
        <div style={{ marginTop: 16 }}>{t('common.loading', 'Loading...')}</div>
      ) : (
        <>
          {activeTab === 1 ? (
            <>
              {(!discoverGrouped || Object.values(discoverGrouped).every(arr => arr.length === 0)) ? (
                <Empty>
                  <h3>{t('tournamentEvents.noEventsTitle', 'No tournament events')}</h3>
                  <p>{t('tournamentEvents.noEventsDesc', 'No matching tournaments for your filters.')}</p>
                </Empty>
              ) : (
                <>
                  {['live', 'coming_soon', 'pending', 'finished'].map((k) => (
                    (isStatusEnabled(k) && discoverGrouped?.[k]?.length ? (
                      <div key={k} style={{ marginTop: 18 }}>
                        <SectionTitle>
                          {statusLabel(k)} ({discoverGrouped[k].length})
                        </SectionTitle>
                        <Grid>
                          {discoverGrouped[k].map(ev => (
                            <EventCard
                              key={ev.id}
                              ev={ev}
                              mode="discover"
                              price={parsePrice(ev)}
                              computedStatus={k}
                              showManage={false}
                              t={t}
                              statusLabel={statusLabel}
                            />
                          ))}
                        </Grid>
                      </div>
                    ) : null)
                  ))}
                </>
              )}
            </>
          ) : (
            <Grid>
              {myFiltered.length === 0 ? (
                <Empty>
                  <h3>{t('tournamentEvents.noEventsTitle', 'No tournament events')}</h3>
                  <p>{t('tournamentEvents.noEventsDesc', 'No matching tournaments for your filters.')}</p>
                </Empty>
              ) : (
                myFiltered
                  .map(ev => (
                    <EventCard
                      key={ev.id}
                      ev={ev}
                      mode="mine"
                      price={parsePrice(ev)}
                      computedStatus={classify(ev)}
                      showManage={!!ev?.createdBy && !!user?.id && String(ev.createdBy) === String(user.id)}
                      t={t}
                      statusLabel={statusLabel}
                    />
                  ))
              )}
            </Grid>
          )}
        </>
      )}
    </Container>
  );
};

const EventCard = ({ ev, price, computedStatus, showManage, t, statusLabel }) => {
  const heroUrl = ev?.bannerImage || null;
  return (
    <Card>
      <CardHero $url={heroUrl} />
      <Top>
        <Badge>
          {ev.category?.icon} {ev.category?.name || 'Tournament'}
        </Badge>
        <RightTop>
          <PricePill paid={price > 0}>
            {price > 0 ? `${price} ${t('common.gcoins', 'G-Coins')}` : t('common.free', 'Free')}
          </PricePill>
          <StatusPill status={computedStatus}>{statusLabel(computedStatus)}</StatusPill>
        </RightTop>
      </Top>
      <Name>{ev.name}</Name>
      {ev.description ? (
        <Desc>
          {String(ev.description).slice(0, 110)}{String(ev.description).length > 110 ? '…' : ''}
        </Desc>
      ) : null}
      <Meta>
        <span>📅 {ev.startDate ? new Date(ev.startDate).toLocaleString() : t('common.tbd', 'TBD')}</span>
      </Meta>
      <Actions>
        <Link to={`/events/${ev.slug}/watch`}>
          <Button variant="primary" size="small">{t('events.watch', 'Watch')}</Button>
        </Link>
        <Link to={`/events/${ev.slug}`}>
          <Button variant="secondary" size="small">{t('events.details', 'Details')}</Button>
        </Link>
        <Link to={`/events/${ev.slug}`}>
          <Button variant="secondary" size="small">{t('tournamentEvents.bracket', 'Bracket')}</Button>
        </Link>
        {showManage ? (
          <Link to={`/events/${ev.slug}/dashboard`}>
            <Button variant="secondary" size="small">{t('common.manage', 'Manage')}</Button>
          </Link>
        ) : null}
      </Actions>
    </Card>
  );
};

const Grid = styled.div`
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
`;

const Card = styled.div`
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.cardBg};
  padding: 14px;
`;

const CardHero = styled.div`
  height: 110px;
  border-radius: 10px;
  margin-bottom: 10px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ $url }) =>
    $url
      ? `linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.6)), url(${$url}) center/cover no-repeat`
      : 'linear-gradient(135deg, rgba(102,126,234,0.35) 0%, rgba(118,75,162,0.35) 100%)'};
`;

const Top = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

const RightTop = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const Badge = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
`;

const StatusPill = styled.div`
  font-size: 12px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  color: white;
  text-transform: capitalize;
  background: ${({ status }) =>
    status === 'live' ? '#F59E0B' :
    status === 'coming_soon' ? '#10B981' :
    status === 'pending' ? '#3B82F6' :
    '#6B7280'
  };
`;

const PricePill = styled.div`
  font-size: 12px;
  font-weight: 800;
  padding: 4px 10px;
  border-radius: 999px;
  color: ${({ paid }) => (paid ? '#111827' : '#111827')};
  background: ${({ paid }) => (paid ? '#FBBF24' : '#E5E7EB')};
`;

const Name = styled.h3`
  margin: 10px 0 6px 0;
  color: ${({ theme }) => theme.text};
`;

const Desc = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 14px;
  margin-bottom: 10px;
`;

const Meta = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 14px;
`;

const Actions = styled.div`
  margin-top: 12px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const SectionTitle = styled.h3`
  margin: 0 0 10px 0;
  color: ${({ theme }) => theme.text};
  font-size: 16px;
`;

const Empty = styled.div`
  padding: 30px;
  border-radius: 12px;
  border: 1px dashed ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.cardBg};
`;

const FilterBar = styled.div`
  margin-top: 14px;
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 260px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.cardBg};
  color: ${({ theme }) => theme.text};
`;

const Pills = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Pill = styled.button`
  padding: 8px 10px;
  border-radius: 999px;
  border: 1px solid ${({ theme, active }) => (active ? theme.primary : theme.border)};
  background: ${({ active }) => (active ? 'rgba(102,126,234,0.15)' : 'transparent')};
  color: ${({ theme }) => theme.text};
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
`;

export default TournamentEvents;



